/* Learner progress, persisted in localStorage.
 *
 * Shape:
 *   solved:       {levelId: isoDate}
 *   code:         {levelId: source}                 editor drafts
 *   quiz:         {levelId: {questionIndex: optionIndex}}
 *   lastLevel:    {trackId: levelId}
 *   levelStats:   {levelId: {hints, revealed, solvedWithoutHints, solvedWithoutReveal, perfectQuiz, quizAttempts}}
 *   cards:        {cardId: {box, due, reviews, lapses, last, recovered}}   see lib/learning.js
 *   reviewDays:   ["YYYY-MM-DD", ...]
 *   achievements: {achievementId: isoDate}
 *
 * Storage can be unavailable (private mode, blocked site data), so every access is guarded and the
 * app keeps working in memory.
 */
import { useSyncExternalStore } from "react";

export const STORAGE_KEY = "code-forge:progress:v1";

const EMPTY = {
  solved: {},
  code: {},
  quiz: {},
  lastLevel: {},
  levelStats: {},
  cards: {},
  reviewDays: [],
  achievements: {},
};

function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    return { ...EMPTY, ...JSON.parse(raw) };
  } catch {
    return EMPTY;
  }
}

let state = read();
const listeners = new Set();

function commit(next, { notify = true } = {}) {
  state = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Keep the in-memory state; progress simply will not survive a reload.
  }
  if (notify) listeners.forEach((listener) => listener());
}

function update(section, key, value, options) {
  const entries = { ...state[section] };
  if (value === undefined) delete entries[key];
  else entries[key] = value;
  commit({ ...state, [section]: entries }, options);
}

function updateStats(levelId, patch) {
  const current = state.levelStats[levelId] || {};
  update("levelStats", levelId, { ...current, ...(typeof patch === "function" ? patch(current) : patch) });
}

export const progress = {
  get: () => state,
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** First completion also records how the level was solved, for the study-habit achievements. */
  markSolved(levelId) {
    if (state.solved[levelId]) return;
    const stats = state.levelStats[levelId] || {};
    commit({
      ...state,
      solved: { ...state.solved, [levelId]: new Date().toISOString() },
      levelStats: {
        ...state.levelStats,
        [levelId]: { ...stats, solvedWithoutHints: !stats.hints, solvedWithoutReveal: !stats.revealed },
      },
    });
  },
  recordHint: (levelId) => updateStats(levelId, (s) => ({ hints: (s.hints || 0) + 1 })),
  recordReveal: (levelId) => updateStats(levelId, { revealed: true }),
  recordPerfectQuiz: (levelId) => updateStats(levelId, { perfectQuiz: true }),

  // Saved on every keystroke; nothing renders from it, so subscribers are not woken up.
  saveCode: (levelId, source) => update("code", levelId, source, { notify: false }),
  clearCode: (levelId) => update("code", levelId, undefined, { notify: false }),

  answerQuestion(levelId, questionIndex, optionIndex) {
    update("quiz", levelId, { ...(state.quiz[levelId] || {}), [questionIndex]: optionIndex });
  },
  resetQuiz(levelId) {
    const stats = state.levelStats[levelId] || {};
    const quiz = { ...state.quiz };
    delete quiz[levelId];
    const solved = { ...state.solved };
    delete solved[levelId];
    commit({
      ...state,
      quiz,
      solved,
      levelStats: { ...state.levelStats, [levelId]: { ...stats, quizAttempts: (stats.quizAttempts || 0) + 1 } },
    });
  },
  setLastLevel(trackId, levelId) {
    if (state.lastLevel[trackId] !== levelId) update("lastLevel", trackId, levelId);
  },

  recordReview(cardId, schedule, dayKey) {
    const reviewDays = state.reviewDays.includes(dayKey) ? state.reviewDays : [...state.reviewDays, dayKey];
    commit({ ...state, cards: { ...state.cards, [cardId]: schedule }, reviewDays });
  },

  /** Store newly earned achievements and return their ids. */
  recordAchievements(ids) {
    const fresh = ids.filter((id) => !state.achievements[id]);
    if (fresh.length) {
      const now = new Date().toISOString();
      commit({ ...state, achievements: { ...state.achievements, ...Object.fromEntries(fresh.map((id) => [id, now])) } });
    }
    return fresh;
  },

  /** Test helper: forget everything, or start from a given state. */
  reset: (next = EMPTY) => commit({ ...EMPTY, ...next }),
};

export function useProgress() {
  return useSyncExternalStore(progress.subscribe, progress.get, progress.get);
}
